/**
 * Honest activation-context display projection for the Signal modal (T1).
 *
 * Pure string helpers over the get_signal_detail v13 ActivationContext, kept
 * separate from the component so the rendering rules are node-testable. The panel
 * renders content only when there is a resolved relationship; a null relationship
 * or an absent object is the honest empty state, never a default implying warmth.
 */

import type { ActivationContext } from "./types";

/** True when there is a resolved relationship to render. A null relationship or
 *  an absent/undefined object is the empty state. */
export function hasActivationContent(
  ac: ActivationContext | null | undefined,
): ac is ActivationContext {
  return Boolean(ac && ac.relationship != null);
}

export function relationshipLabel(
  relationship: ActivationContext["relationship"],
): string {
  switch (relationship) {
    case "existing_client":
      return "Existing client";
    case "prior_relationship":
      return "Prior relationship";
    case "net_new":
      return "No prior relationship — net-new for the firm";
    default:
      return "";
  }
}

/** Composes the engagement line from the structured counts. "No delivery on
 *  record" when there is neither active nor dormant work. */
export function engagementLabel(
  d: ActivationContext["engagementDetail"] | null | undefined,
): string {
  const activeCount = d?.activeCount ?? 0;
  const practiceCount = d?.practiceCount ?? 0;
  const dormantCount = d?.dormantCount ?? 0;
  if (activeCount === 0 && dormantCount === 0) return "No delivery on record";
  if (activeCount === 0) {
    return `${dormantCount} dormant engagement${dormantCount === 1 ? "" : "s"}, no active work`;
  }
  let s = `${activeCount} active engagement${activeCount === 1 ? "" : "s"}`;
  if (practiceCount > 0) {
    s += ` across ${practiceCount} practice${practiceCount === 1 ? "" : "s"}`;
  }
  if (dormantCount > 0) s += ` · ${dormantCount} dormant`;
  return s;
}

/** Renders the tier as stored ("Tier 1/2/3") or an honest absence. Never
 *  "Tier A/B", never "Unranked". */
export function valueTierLabel(valueTier: ActivationContext["valueTier"]): string {
  return valueTier ?? "No tier on record";
}
